import { FormikProps } from "formik";
import { ICCheckbox, ICTextInput } from "../../core-component/form";
import { ICButton } from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import {
  PACKAGE_CONFIG_LABEL_KEY,
  PACKAGE_CREDIT_KEYS,
  PACKAGE_LIMIT_KEYS,
  STOCK_VALUATION_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import {
  CONFIG_VALUE_MODE_ENUM,
  PACKAGE_CONFIG_KEY_ENUM,
  STOCK_VALUATION_METHOD_ENUM,
} from "../../utils/PurchaseEnumerations";
import {
  allowsContactToSales,
  IConfigValue,
  IPackageEditorForm,
} from "./PackageEditorController";

interface IPackageLimitsTabProps {
  formikProps: FormikProps<IPackageEditorForm>;
  readOnly: boolean;
}

interface IModeButtonParams {
  label: string;
  isOn: boolean;
  isOff: boolean;
  onClick: () => void;
}

const MODE_LABEL_KEY: Record<CONFIG_VALUE_MODE_ENUM, string> = {
  [CONFIG_VALUE_MODE_ENUM.Number]: "mode_number",
  [CONFIG_VALUE_MODE_ENUM.Unlimited]: "unlimited",
  [CONFIG_VALUE_MODE_ENUM.ContactToSales]: "contact_to_sales",
  [CONFIG_VALUE_MODE_ENUM.NotProvided]: "not_provided",
};

const STOCK_MODES: readonly CONFIG_VALUE_MODE_ENUM[] = [
  CONFIG_VALUE_MODE_ENUM.Unlimited,
  CONFIG_VALUE_MODE_ENUM.Number,
  CONFIG_VALUE_MODE_ENUM.NotProvided,
];

const STOCK_MODE_LABEL_KEY: Partial<Record<CONFIG_VALUE_MODE_ENUM, string>> = {
  [CONFIG_VALUE_MODE_ENUM.Unlimited]: "stock_mode_all",
  [CONFIG_VALUE_MODE_ENUM.Number]: "stock_mode_selected",
  [CONFIG_VALUE_MODE_ENUM.NotProvided]: "not_provided",
};

const PackageLimitsTab: React.FC<IPackageLimitsTabProps> = ({ formikProps, readOnly }) => {
  const { t } = UseTranslationHook();
  const { values, setFieldValue } = formikProps;
  const contactSalesAllowed = allowsContactToSales(values.visibility_type);

  const modeButton = ({ label, isOn, isOff, onClick }: IModeButtonParams) => (
    <ICButton
      type="button"
      key={label}
      disabled={readOnly || isOff}
      onClick={onClick}
      className={cn("w-fit px-2 py-1 rounded-none text-xs border-r last:border-r-0 whitespace-nowrap", {
        "bg-accent-emphasis text-on-accent": isOn,
        "bg-transparent text-muted": !isOn,
        "line-through opacity-60 cursor-not-allowed": isOff,
      })}
    >
      {label}
    </ICButton>
  );

  const configRow = (key: PACKAGE_CONFIG_KEY_ENUM) => {
    const config: IConfigValue = values.configs[key] ?? {
      mode: CONFIG_VALUE_MODE_ENUM.NotProvided,
      value: "",
    };
    const setConfig = (next: IConfigValue) => setFieldValue(`configs.${key}`, next);

    return (
      <div key={key} className="grid grid-cols-6 gap-3 items-center py-2 border-b">
        <span className="col-span-2 text-sm">{t(PACKAGE_CONFIG_LABEL_KEY[key])}</span>
        <div className="col-span-3 inline-flex flex-wrap border rounded-md overflow-hidden w-fit">
          {Object.values(CONFIG_VALUE_MODE_ENUM).map((mode) =>
            modeButton({
              label: t(MODE_LABEL_KEY[mode]),
              isOn: config.mode === mode,
              isOff:
                mode === CONFIG_VALUE_MODE_ENUM.ContactToSales &&
                !contactSalesAllowed &&
                config.mode !== mode,
              onClick: () =>
                setConfig({
                  mode,
                  value: mode === CONFIG_VALUE_MODE_ENUM.Number ? config.value : "",
                }),
            }),
          )}
        </div>
        <ICTextInput
          type="number"
          min={0}
          value={config.mode === CONFIG_VALUE_MODE_ENUM.Number ? config.value : ""}
          placeholder={config.mode === CONFIG_VALUE_MODE_ENUM.Number ? "0" : "-"}
          disabled={readOnly || config.mode !== CONFIG_VALUE_MODE_ENUM.Number}
          onChange={(event) => setConfig({ mode: config.mode, value: event.target.value })}
        />
      </div>
    );
  };

  const stock = values.stock_valuation;
  const stockRow = (
    <div className="grid grid-cols-6 gap-3 items-center py-2 border-b">
      <span className="col-span-2 text-sm">
        {t(PACKAGE_CONFIG_LABEL_KEY[PACKAGE_CONFIG_KEY_ENUM.AllowedStockValuationMethods])}
      </span>
      <div className="col-span-4 space-y-2">
        <div className="inline-flex border rounded-md overflow-hidden w-fit">
          {STOCK_MODES.map((mode) =>
            modeButton({
              label: t(STOCK_MODE_LABEL_KEY[mode] ?? ""),
              isOn: stock.mode === mode,
              isOff: false,
              onClick: () => setFieldValue("stock_valuation", { mode, methods: stock.methods }),
            }),
          )}
        </div>
        {stock.mode === CONFIG_VALUE_MODE_ENUM.Number && (
          <div className="flex flex-wrap gap-4">
            {Object.values(STOCK_VALUATION_METHOD_ENUM).map((method) => (
              <ICCheckbox
                key={method}
                label={t(STOCK_VALUATION_LABEL_KEY[method])}
                checked={stock.methods.includes(method)}
                disabled={readOnly}
                onChange={(checked) =>
                  setFieldValue("stock_valuation", {
                    mode: stock.mode,
                    methods: checked
                      ? [...stock.methods, method]
                      : stock.methods.filter((item) => item !== method),
                  })
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <div className="font-semibold">{t("limits")}</div>
        {PACKAGE_LIMIT_KEYS.map(configRow)}
        {stockRow}
      </div>
      <div>
        <div className="font-semibold">{t("included_credits")}</div>
        <div className="text-sm text-muted">{t("included_credits_hint")}</div>
        {PACKAGE_CREDIT_KEYS.map(configRow)}
      </div>
      <div className="text-sm text-muted">{t("contact_to_sales_hint")}</div>
    </div>
  );
};

export default PackageLimitsTab;
